import React from 'react';
import { PropTypes } from 'prop-types';
import pluralize from 'pluralize';
import formatShortDate from 'common/utils/Dates/formatShortDate';
import formatSpend from 'common/utils/Money/formatSpend';
import { LineItem } from 'common/display/LineItem';
import { useFeatureFlag } from 'common/hooks';
import { isRequestedEdition } from '../../../utils/serviceAuthorizationUtils';
import useAuthorizationCapUtilization from '../shared/useAuthorizationCapUtilization';

const filterByState = (items) => {
  const approvedItems = items.filter((item) => item.state === 'approved');
  const requestedItems = items.filter((item) => item.state === 'requested');
  return { approvedItems, requestedItems };
};

function AuthorizationSpendSection({
  feeScheduleProgram,
  person,
  isUnitBased,
  requestedStartsAt,
  requestedEndsAt,
  authorizedAmount,
  setIsAmountExceeded,
  authorization,
}) {
  const updateEditAuthorizationsEnabled = useFeatureFlag('pays-5650-update-edit-authorizations');
  const shouldExcludeServiceAuthorizations = updateEditAuthorizationsEnabled &&
    (isRequestedEdition(authorization) || authorization?.state === 'approved');

  const {
    isFetching,
    capInfo,
    authorizedSpends,
    totalApprovedSpend,
    totalRequestedSpend,
  } = useAuthorizationCapUtilization({
    feeScheduleProgram,
    person,
    isUnitBased,
    requestedStartsAt,
    requestedEndsAt,
    authorizedAmount,
    setIsAmountExceeded,
    shouldExcludeServiceAuthorizations,
    authorization,
  });

  const { approvedItems, requestedItems } = filterByState(authorizedSpends);

  return (
    <>
      <hr className="my-5 border-dark-fill-blue border-solid" />
      <h4 className="leading-4">
        CLIENT SERVICE UTILIZATION
      </h4>
      <p className="mt-2 mb-3">
        The following requests have service delivery dates that overlap with this cap period:
        <b> {
          capInfo.hasCap ?
            capInfo.formattedCapPeriod : `${formatShortDate(requestedStartsAt)} - ${formatShortDate(requestedEndsAt)}`
        }
        </b>
      </p>
      <dl className="grid grid-cols-2 gap-2 mt-0">
        { capInfo.hasCap && (
          <LineItem isLoaded={!isFetching} field="Available Amount">
            {capInfo.formattedAvailableAmount}
          </LineItem>
        )}
        <LineItem isLoaded={!isFetching} field="Accepted Requests">
          {updateEditAuthorizationsEnabled && isRequestedEdition(authorization) && approvedItems.length > 0 ? (
            `Update pending (${approvedItems.length} ${pluralize('request', approvedItems.length)})`
          ) : (
            <>
              {formatSpend(totalApprovedSpend, feeScheduleProgram.unit, !isUnitBased)}
              {` (${approvedItems.length} ${pluralize('request', approvedItems.length)})`}
            </>
          )}
        </LineItem>
        <LineItem isLoaded={!isFetching} field="Open Requests">
          {formatSpend(totalRequestedSpend, feeScheduleProgram.unit, !isUnitBased)}
          {` (${requestedItems.length} ${pluralize('request', requestedItems.length)})`}
        </LineItem>
        { capInfo.hasCap && (
          <>
            <LineItem isLoaded={!isFetching} field="Cap">
              {capInfo.formattedCap}
            </LineItem>
            <LineItem isLoaded={!isFetching} field="Cap Period">
              {capInfo.formattedCapPeriod}
            </LineItem>
          </>
        )}
      </dl>
    </>
  );
}

AuthorizationSpendSection.propTypes = {
  feeScheduleProgram: PropTypes.object.isRequired,
  person: PropTypes.object.isRequired,
  isUnitBased: PropTypes.bool.isRequired,
  requestedStartsAt: PropTypes.object.isRequired,
  requestedEndsAt: PropTypes.object.isRequired,
  authorizedAmount: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  setIsAmountExceeded: PropTypes.func.isRequired,
  authorization: PropTypes.object,
};

AuthorizationSpendSection.defaultProps = {
  authorization: null,
  authorizedAmount: null,
};

export default AuthorizationSpendSection;
